import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, from, forkJoin } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { hasOwnProperty } from '../helper-functions';
import { PushNotificationSchema } from '@capacitor/push-notifications';
import { Device, DeviceId } from '@capacitor/device';
import { AuthService } from '../oidc/auth.service';
import { AUTHENTICATED } from '../oidc/auth.interceptor';

const API_URL = environment.apiUrl;
// const API_SERVICE = `http://localhost:8090/v4`;
const API_SERVICE = `${API_URL}/push-notification/v4`;
const API_REGISTER = `${API_SERVICE}/register`;
const API_REGISTER_PARAM = `${API_SERVICE}/register/{0}`;
const API_STATISTICS = `${API_SERVICE}/statistic/{0}`;
const API_USER = `${API_SERVICE}/user`;

export enum PushChannel {
  GENERAL = 'GENERAL',
  SPORTS = 'SPORTS',
  LIBRARY = 'LIBRARY',
  TIMETABLE = 'TIMETABLE',
  HEALTH_PASSPORT = 'HEALTH_PASSPORT',
  CAMPUSID = 'CAMPUSID',
  IOS = 'IOS',
  ANDROID = 'ANDROID',
  TICKET = 'TICKET',
}

@Injectable({
  providedIn: 'root',
})
export class PushService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  public register(pushId: string | null, channelList?: PushChannel[]): Observable<void> {
    return forkJoin({
      deviceInfo: from(Device.getInfo()),
      uuid: from(Device.getId())
    }).pipe(switchMap((infos) => {
      const channel = [...(channelList ?? []), infos.deviceInfo.operatingSystem === 'ios' ? PushChannel.IOS : PushChannel.ANDROID];
      const deviceId = infos.uuid.identifier;
      return this.http.post<void>(API_REGISTER, { deviceId, pushId, channel }, {
        context: this.authService.isAuthenticated() ? AUTHENTICATED : undefined
      });
    }));
  }

  public unregister() {
    return from(Device.getId()).pipe(
      switchMap((uuid: DeviceId) => {
        const deviceId = uuid.identifier;
        return this.http.delete(API_REGISTER_PARAM.format(deviceId));
      })
    );
  }

  public updateStatistics(notification: PushNotificationSchema): void {
    if (!hasOwnProperty(notification.data, 'messageId')) {
      return;
    }
    const messageId = notification.data.messageId;
    from(Device.getId())
      .pipe(
        switchMap((uuid: DeviceId) => {
          const headers = {
            'Content-Type': 'application/json',
            'X-DeviceId': uuid.identifier,
          };
          return this.http.put<any>(API_STATISTICS.format(messageId), null, { headers });
        })
      )
      .subscribe();
  }

  public sendTestNotification(): Observable<void> {
    return from(Device.getId()).pipe(
      switchMap((uuid: DeviceId) => {
        const deviceId = uuid.identifier;
        return this.http.get<void>(`${API_USER}/test`, { params: { deviceId }, context: AUTHENTICATED });
      })
    );
  }
}
